// Copyright (c) Microgreen Manager LLC. All rights reserved. Proprietary and confidential. Unauthorized copying is strictly prohibited.
import { useLazyQuery, useMutation } from '@apollo/client';
import type { SelectChangeEvent } from '@mui/material/Select';
import { clientLogger } from '@seedplanner/logger';
import { SOW_DENSITY_UNITS_VALUES, type WeightUnit } from '@seedplanner/shared-utils';
import gql from 'graphql-tag';
import { enqueueSnackbar } from 'notistack';
import * as React from 'react';
import { useUserContext } from '../../context/UserContext';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '../../lib/base';
import type {
  CropSowDensityDataQuery,
  CropSowDensityDataQueryVariables,
  EditCropSowDensityMutation,
  EditCropSowDensityMutationVariables,
} from '../../types/__generated__/graphql';
import { getNumericID, shortenWeightUnit, validateNumInput } from '../../utils';
import { ExitDialogButton } from '../common/button';
import { ErrorView } from '../common/error';
import { PageLoading } from '../common/loading';
import { formatCropDialogTitle } from './formatCropDialogTitle';

const EDIT_CROP_SOW_DENSITY_MUTATION = gql`
  mutation EditCropSowDensity(
    $crop_id: Int!
    $sow_density: Float!
    $sow_density_unit: String!
  ) {
    editCropSowDensity(
      input: {
        crop_id: $crop_id
        sow_density: $sow_density
        sow_density_unit: $sow_density_unit
      }
    ) {
      updated_crop {
        id
        sowing_density
        sow_density_unit
        updated_at
      }
    }
  }
`;

const CROP_SOW_DENSITY_DATA = gql`
  query CropSowDensityData($id: ID!) {
    crop(id: $id) {
      id
      sowing_density
      sow_density_unit
    }
  }
`;

interface Props {
  readonly cropId: string;
  readonly cropName: string;
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly onSuccess: () => void;
}

export default function EditCropSowDensityDialog({ isOpen, onClose, onSuccess, cropId, cropName }: Props) {
  const [fetchCrop, { data, error: dataError, loading: cropDataLoading }] = useLazyQuery<
    CropSowDensityDataQuery,
    CropSowDensityDataQueryVariables
  >(CROP_SOW_DENSITY_DATA, {
    fetchPolicy: 'network-only',
  });
  const [editCropSowDensity, { loading: editCropLoading }] = useMutation<
    EditCropSowDensityMutation,
    EditCropSowDensityMutationVariables
  >(EDIT_CROP_SOW_DENSITY_MUTATION);
  const { preferredWeightUnit } = useUserContext();

  React.useEffect(() => {
    if (isOpen) {
      void fetchCrop({
        variables: { id: cropId },
        onCompleted: (cropData) => {
          const crop = cropData?.crop;
          if (crop != null) {
            const sowDensity = crop.sowing_density ?? '';
            const sowDensityUnit = crop.sow_density_unit ?? preferredWeightUnit;
            setSowDensity(sowDensity);
            setSowDensityUnit(sowDensityUnit as WeightUnit);
          }
        },
        onError: (errorIn) => {
          enqueueSnackbar(errorIn.message, { variant: 'error', autoHideDuration: 10000 });
        },
      });
    }
  }, [isOpen, cropId, preferredWeightUnit, fetchCrop]);

  const disableFields = cropDataLoading || editCropLoading;

  const [sowDensity, setSowDensity] = React.useState('');
  const [unit, setSowDensityUnit] = React.useState<WeightUnit>(preferredWeightUnit);

  const onUnitChange = (event: SelectChangeEvent<string>) => {
    setSowDensityUnit(event.target.value as WeightUnit);
  };
  const onChangeSowDensity = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setSowDensity(e.target.value);
    validateNumInput({
      event: e,
      setValidationMessage: (msg) => setSowDensityValidation(msg),
      validateGT: 0,
    });
  };
  const [sowDensityValidation, setSowDensityValidation] = React.useState('');
  const onSowDensityBlur = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    validateNumInput({
      event: e,
      setValidationMessage: (msg) => setSowDensityValidation(msg),
      validateGT: 0,
    });
  };

  const onSubmit = (_e?: React.MouseEvent | React.TouchEvent) => {
    void editCropSowDensity({
      variables: {
        sow_density: sowDensity === '' ? -1 : Number.parseFloat(sowDensity),
        sow_density_unit: unit,
        crop_id: getNumericID(cropId),
      },
      onCompleted: (dataIn) => {
        if (dataIn !== undefined && dataIn.editCropSowDensity != null) {
          enqueueSnackbar('Updated sow density', { variant: 'success' });
          onSuccess();
        } else {
          enqueueSnackbar('Error editing crop, no data returned', { variant: 'error', autoHideDuration: 10000 });
        }
        setSowDensity('');
        setSowDensityValidation('');
        setSowDensityUnit(preferredWeightUnit);
      },
      onError: (errorIn) => {
        enqueueSnackbar(errorIn.message, { variant: 'error', autoHideDuration: 10000 });
        clientLogger.error({ title: 'edit_crop_error', message: errorIn.message });
      },
    });
  };

  const hasInputValidationError: boolean = sowDensityValidation !== '';
  const requiredFieldsFilled: boolean = sowDensity !== '';
  const isSubmitDisabled: boolean = !requiredFieldsFilled || hasInputValidationError || disableFields;
  const onEnter = () => {
    if (!isSubmitDisabled) {
      onSubmit();
    }
  };
  const onCancelKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      onClose();
    }
  };
  const onSubmitKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      if (!isSubmitDisabled) {
        onSubmit();
      }
    }
  };

  const form = (
    <Box display="flex" flexDirection="column" sx={{ m: 1 }} gap={2}>
      <Grid display="flex" flexDirection="row" flexWrap="nowrap" gap={2}>
        <Grid flexGrow="1">
          <TextField
            aria-describedby="sow-density"
            id="sow-density"
            label={`Sow Density (${unit} per tray)`}
            value={sowDensity}
            onChange={onChangeSowDensity}
            onBlur={onSowDensityBlur}
            onEnter={onEnter}
            subtext={sowDensityValidation}
            error={sowDensityValidation !== ''}
            type="number"
            inputMode="decimal"
            numberMin={0}
            hasSubtext
            disabled={disableFields}
          />
        </Grid>
        <Grid>
          <FormControl fullWidth>
            <InputLabel id="units-selector">Units</InputLabel>
            <Select
              aria-describedby="units-selector"
              id="units-selector"
              labelId="units-selector"
              label="Units"
              value={unit}
              onChange={onUnitChange}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  if (!isSubmitDisabled) {
                    onSubmit();
                  }
                }
              }}
              required={true}
              disabled={disableFields}
            >
              {SOW_DENSITY_UNITS_VALUES.map((sizeUnit) => {
                return (
                  <MenuItem key={sizeUnit} value={sizeUnit}>
                    {shortenWeightUnit(sizeUnit)}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
        </Grid>
      </Grid>
    </Box>
  );

  return (
    <Dialog
      open={isOpen}
      onClose={() => {
        onClose();
      }}
      maxWidth="md"
    >
      <DialogTitle>
        <Box display="flex" flexDirection="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">{formatCropDialogTitle('Edit Crop Sow Density', cropName)}</Typography>
          <ExitDialogButton dontShiftUp onCloseDialog={onClose} />
        </Box>
      </DialogTitle>
      <DialogContent sx={{ padding: 1 }}>
        {dataError ? <ErrorView error={dataError} /> : cropDataLoading || data?.crop == null ? <PageLoading /> : form}
      </DialogContent>
      <DialogActions disableSpacing sx={{ flexDirection: { md: 'row', xs: 'column' }, alignItems: 'center' }}>
        <Button
          sx={{ width: { xs: '100%', md: '50%' } }}
          variant="outlined"
          onClick={onClose}
          onKeyDown={onCancelKeyDown}
        >
          Cancel
        </Button>
        <Button
          sx={{ width: { xs: '100%', md: '50%' }, ml: { md: 1 }, mt: { xs: 1, md: 0 } }}
          variant="contained"
          onClick={onSubmit}
          type="submit"
          disabled={isSubmitDisabled}
          onKeyDown={onSubmitKeyDown}
        >
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  );
}
